import DOMPurify from 'dompurify';

/**
 * Configuration options for the sanitizer
 */
interface ISanitizeOptions {
  /** Allow target="_blank" on links */
  allowTargetBlank?: boolean;
  /** Additional allowed tags beyond the defaults */
  extraAllowedTags?: string[];
  /** Additional allowed attributes beyond the defaults */
  extraAllowedAttrs?: string[];
}

/**
 * Maximum length for regex attribute matching to prevent ReDoS
 */
const MAX_ATTR_LENGTH = 500;
const MAX_TAG_LENGTH = 100;
const MAX_LINE_LENGTH = 2000;
const MAX_ENTITY_LENGTH = 50;

/**
 * Regex patterns used throughout the sanitizer
 * Organized by use case for clarity and to avoid ReDoS vulnerabilities
 * All patterns use length limits to prevent super-linear runtime
 */
const REGEX_PATTERNS = {
  // HTML-related patterns (with length limits to prevent ReDoS)
  html: {
    /** Matches anchor tags with target="_blank" attribute - limited to 500 chars per group */
    linkWithTargetBlank: new RegExp(`<a([^>]{0,${MAX_ATTR_LENGTH}})target="_blank"([^>]{0,${MAX_ATTR_LENGTH}})>`, 'gi'),
    /** Matches any HTML tag - limited to 100 chars */
    htmlTag: new RegExp(`<[a-z][^>]{0,${MAX_TAG_LENGTH}}>`, 'i'),
    /** Matches list tags (ul or ol) */
    listTag: /<[uo]l>/,
  },

  // List detection patterns
  lists: {
    /** Matches dash separator pattern " - " */
    dashSeparator: /\s-\s/g,
    /** Matches ordered list markers like "1)" or "1." - limited digits */
    orderedMarker: /\d{1,4}[.)]\s/g,
    /** Matches unordered list line starting with "-" or "•" - limited line length */
    unorderedLine: new RegExp(`^\\s{0,20}[-•]\\s([^\\n]{1,${MAX_LINE_LENGTH}})$`),
    /** Matches ordered list line starting with number - limited line length */
    orderedLine: new RegExp(`^\\s{0,20}(\\d{1,4})[.)]\\s([^\\n]{1,${MAX_LINE_LENGTH}})$`),
  },

  // Text formatting patterns
  formatting: {
    /** Matches double or more newlines - limited repetition */
    doubleNewline: /\n\n{1,10}/,
    /** Matches single newline */
    singleNewline: /\n/g,
  },

  // Paragraph break trigger patterns (with length limits)
  paragraphBreaks: {
    /** Colon followed by text (for paragraph breaks) - limited closing tag */
    colonBreak: new RegExp(`:(<\\/[^>]{1,${MAX_TAG_LENGTH}}>)?(\\s)([A-Za-z"'"'])`, 'g'),
    /** Question mark followed by uppercase (new sentence) - limited closing tag */
    questionBreak: new RegExp(`\\?(<\\/[^>]{1,${MAX_TAG_LENGTH}}>)?(\\s)([A-Z])`, 'g'),
  },
};

/**
 * Splits content by list blocks (ul/ol) without using vulnerable regex
 * This avoids ReDoS by using string indexOf instead of regex matching
 * @param content - HTML content that may contain list blocks
 * @returns Array of segments (text and list blocks)
 */
function splitByListBlocks (content: string): string[] {
  const segments: string[] = [];
  let remaining = content;
  let safetyCounter = 0;
  const maxIterations = 1000; // Prevent infinite loops

  while (remaining.length > 0 && safetyCounter < maxIterations) {
    safetyCounter++;

    // Find the next list opening tag
    const ulStart = remaining.indexOf('<ul>');
    const olStart = remaining.indexOf('<ol>');

    let listStart = -1;
    let listTag = '';
    let closingTag = '';

    if (ulStart !== -1 && (olStart === -1 || ulStart < olStart)) {
      listStart = ulStart;
      listTag = '<ul>';
      closingTag = '</ul>';
    } else if (olStart !== -1) {
      listStart = olStart;
      listTag = '<ol>';
      closingTag = '</ol>';
    }

    if (listStart === -1) {
      // No more lists, add remaining content
      if (remaining) {
        segments.push(remaining);
      }
      break;
    }

    // Add content before the list
    if (listStart > 0) {
      segments.push(remaining.substring(0, listStart));
    }

    // Find the closing tag
    const listEnd = remaining.indexOf(closingTag, listStart + listTag.length);

    if (listEnd === -1) {
      // No closing tag found, add rest as-is
      segments.push(remaining.substring(listStart));
      break;
    }

    // Add the complete list block
    const listBlock = remaining.substring(listStart, listEnd + closingTag.length);
    segments.push(listBlock);

    // Continue with remaining content
    remaining = remaining.substring(listEnd + closingTag.length);
  }

  return segments;
}

/**
 * Paragraph trigger phrases that indicate a new paragraph should start
 * These are used with a period prefix pattern
 */
const PARAGRAPH_TRIGGER_PHRASES = [
  // Examples and illustrations
  'For example,',
  'For instance,',
  'Examples include',
  // Instructions and activities
  'In pairs,',
  'In groups,',
  'Working together,',
  'Once completed,',
  'When finished,',
  'After this,',
  'Next,',
  'Then,',
  'Finally,',
  'Now,',
  // Transitions and new topics
  'In hospitality,',
  'In this context,',
  'This means',
  'This includes',
  'Remember,',
  'Note that',
  'Keep in mind',
  // Sequential instructions
  'The first',
  'The second',
  'The third',
  'The next',
  'The final',
];

/**
 * Pattern for "Take X minutes" - handled separately due to variable number
 */
const TAKE_MINUTES_PATTERN = /\.\s(Take \d+ minutes?)/gi;

/**
 * Default allowed HTML tags for rich text content
 */
const DEFAULT_ALLOWED_TAGS = [
  // Text formatting
  'p', 'br', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike',
  'sub', 'sup', 'mark',
  // Headings
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  // Lists
  'ul', 'ol', 'li',
  // Links
  'a',
  // Other common elements
  'blockquote', 'pre', 'code', 'hr',
];

/**
 * Default allowed HTML attributes
 */
const DEFAULT_ALLOWED_ATTRS = [
  'href', 'title', 'class', 'style', 'id',
  'target', 'rel',
];

/**
 * Sanitizes HTML content from the backend to prevent XSS attacks
 * while preserving allowed formatting tags.
 *
 * @param html - The raw HTML string from the backend
 * @param options - Optional configuration for the sanitizer
 * @returns Sanitized HTML string safe for rendering
 *
 * @example
 * // Basic usage
 * const safeHtml = sanitizeHtml('<p>Hello <script>alert("xss")</script></p>');
 * // Returns: '<p>Hello </p>'
 *
 * @example
 * // With custom options
 * const safeHtml = sanitizeHtml(content, { allowTargetBlank: true });
 */
export function sanitizeHtml (html: string, options: ISanitizeOptions = {}): string {
  if (!html) {
    return '';
  }

  const {
    allowTargetBlank = true,
    extraAllowedTags = [],
    extraAllowedAttrs = [],
  } = options;

  const allowedTags = [...DEFAULT_ALLOWED_TAGS, ...extraAllowedTags];
  const allowedAttrs = [...DEFAULT_ALLOWED_ATTRS, ...extraAllowedAttrs];

  // Configure DOMPurify with RETURN_TRUSTED_TYPE: false to ensure string return
  const config = {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: allowedAttrs,
    ALLOW_DATA_ATTR: false,
    // Remove script and style tags completely
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
    RETURN_TRUSTED_TYPE: false,
  };

  let sanitized = DOMPurify.sanitize(html, config) as string;

  // Add security attributes to links
  if (allowTargetBlank) {
    // Add rel="noopener noreferrer" to links with target="_blank" for security
    sanitized = sanitized.replace(
      REGEX_PATTERNS.html.linkWithTargetBlank,
      '<a$1target="_blank" rel="noopener noreferrer"$2>'
    );
  }

  return sanitized;
}

/**
 * Converts inline list patterns to HTML lists
 * Handles patterns like: "Here's what we'll cover: - Item 1 - Item 2 - Item 3"
 * Also handles numbered patterns like: "Steps: 1) First 2) Second 3) Third"
 *
 * @param text - Plain text that may contain inline list patterns
 * @returns Text with inline list patterns converted to HTML lists
 */
export function convertInlineListPatterns (text: string): string {
  if (!text) {
    return '';
  }

  let result = text;

  // Check if we have multiple " - " patterns indicating an inline list
  const dashMatches = text.match(REGEX_PATTERNS.lists.dashSeparator);
  if (dashMatches && dashMatches.length >= 2) {
    // Split by " - " pattern but keep track of the intro text
    const parts = text.split(REGEX_PATTERNS.lists.dashSeparator);
    if (parts.length > 1) {
      const introText = parts[0];
      const listItems = parts.slice(1);

      // Build the HTML
      const listHtml = '<ul>' + listItems.map((item) => `<li>${item.trim()}</li>`).join('') + '</ul>';
      result = introText + listHtml;
    }
  }

  // Pattern for inline ordered lists: "1) item 2) item" or "1. item 2. item"
  const orderedMatches = text.match(REGEX_PATTERNS.lists.orderedMarker);
  if (orderedMatches && orderedMatches.length >= 2 && !dashMatches) {
    // Split by numbered pattern
    const parts = text.split(REGEX_PATTERNS.lists.orderedMarker);
    if (parts.length > 1) {
      const introText = parts[0];
      const listItems = parts.slice(1).filter((item) => item.trim());

      if (listItems.length > 0) {
        const listHtml = '<ol>' + listItems.map((item) => `<li>${item.trim()}</li>`).join('') + '</ol>';
        result = introText + listHtml;
      }
    }
  }

  return result;
}

/**
 * Converts plain text list patterns to HTML lists
 * Handles:
 * - Lines starting with "-" → unordered list
 * - Lines starting with "•" → unordered list
 * - Lines starting with "1)", "2)", etc. → ordered list
 * - Lines starting with "1.", "2.", etc. → ordered list
 * - Inline patterns like "text: - item1 - item2 - item3"
 *
 * @param text - Plain text that may contain list patterns
 * @returns Text with list patterns converted to HTML lists
 */
export function convertListPatterns (text: string): string {
  if (!text) {
    return '';
  }

  // First, try to handle inline list patterns (all on one line)
  // Check if there are multiple " - " patterns on what appears to be a single block
  const hasInlineUnorderedList = (text.match(REGEX_PATTERNS.lists.dashSeparator) || []).length >= 2;
  const hasInlineOrderedList = (text.match(REGEX_PATTERNS.lists.orderedMarker) || []).length >= 2;

  if ((hasInlineUnorderedList || hasInlineOrderedList) && !text.includes('\n- ')) {
    return convertInlineListPatterns(text);
  }

  const lines = text.split('\n');
  const result: string[] = [];
  let inUnorderedList = false;
  let inOrderedList = false;

  for (const line of lines) {
    const unorderedMatch = line.match(REGEX_PATTERNS.lists.unorderedLine);
    const orderedMatch = line.match(REGEX_PATTERNS.lists.orderedLine);

    if (unorderedMatch) {
      // Close ordered list if open
      if (inOrderedList) {
        result.push('</ol>');
        inOrderedList = false;
      }
      // Open unordered list if not already open
      if (!inUnorderedList) {
        result.push('<ul>');
        inUnorderedList = true;
      }
      result.push(`<li>${unorderedMatch[1].trim()}</li>`);
    } else if (orderedMatch) {
      // Close unordered list if open
      if (inUnorderedList) {
        result.push('</ul>');
        inUnorderedList = false;
      }
      // Open ordered list if not already open
      if (!inOrderedList) {
        result.push('<ol>');
        inOrderedList = true;
      }
      result.push(`<li>${orderedMatch[2].trim()}</li>`);
    } else {
      // Close any open lists
      if (inUnorderedList) {
        result.push('</ul>');
        inUnorderedList = false;
      }
      if (inOrderedList) {
        result.push('</ol>');
        inOrderedList = false;
      }
      // Check if this line has inline list patterns
      const hasInlineList = (line.match(REGEX_PATTERNS.lists.dashSeparator) || []).length >= 2;
      if (hasInlineList) {
        result.push(convertInlineListPatterns(line));
      } else {
        result.push(line);
      }
    }
  }

  // Close any remaining open lists
  if (inUnorderedList) {
    result.push('</ul>');
  }
  if (inOrderedList) {
    result.push('</ol>');
  }

  return result.join('\n');
}

/**
 * Intelligently breaks long text into paragraphs based on sentence structure
 * Adds paragraph breaks at natural transition points like:
 * - "For example," / "For instance,"
 * - "In pairs," / "In groups,"
 * - "Once completed," / "When finished,"
 * - Numbered instructions like "First," "Second," "Finally,"
 *
 * @param text - Long text without line breaks
 * @returns Text with paragraph breaks inserted
 */
export function addParagraphBreaks (text: string): string {
  if (!text || text.length < 150) {
    // Don't process short text
    return text;
  }

  // Already has paragraph breaks
  if (text.includes('\n\n')) {
    return text;
  }

  let result = text;

  // Replace each trigger phrase with a paragraph break
  // Using string matching instead of complex regex to avoid ReDoS
  PARAGRAPH_TRIGGER_PHRASES.forEach((phrase) => {
    const searchPattern = '. ' + phrase;
    const replacement = '.\n\n' + phrase;
    // Case-insensitive replacement
    let idx = result.toLowerCase().indexOf(searchPattern.toLowerCase());
    while (idx !== -1) {
      const originalPhrase = result.substring(idx + 2, idx + 2 + phrase.length);
      result = result.substring(0, idx) + '.\n\n' + originalPhrase + result.substring(idx + searchPattern.length);
      idx = result.toLowerCase().indexOf(searchPattern.toLowerCase(), idx + replacement.length);
    }
  });

  // Handle "Take X minutes" pattern separately
  result = result.replace(TAKE_MINUTES_PATTERN, (match, captured) => {
    return `.\n\n${captured}`;
  });

  // Break after colons when followed by text (but not for times like "2:00" or URLs)
  result = result.replace(REGEX_PATTERNS.paragraphBreaks.colonBreak, (match, closingTag, space, firstChar) => {
    return `:${closingTag || ''}\n\n${firstChar}`;
  });

  // Break after question marks when followed by another sentence
  result = result.replace(REGEX_PATTERNS.paragraphBreaks.questionBreak, (match, closingTag, space, firstChar) => {
    return `?${closingTag || ''}\n\n${firstChar}`;
  });

  return result;
}

/**
 * Converts plain text with line breaks to HTML paragraphs
 * Also converts list patterns (-, 1), 2), etc.) to proper HTML lists
 * Useful when backend sends plain text that should be rendered with proper spacing
 *
 * @param text - Plain text with line breaks
 * @returns HTML with paragraph tags and lists
 */
export function textToHtml (text: string): string {
  if (!text) {
    return '';
  }

  // First, add intelligent paragraph breaks to long text
  const withParagraphBreaks = addParagraphBreaks(text);

  // Then, convert list patterns to HTML lists
  const withLists = convertListPatterns(withParagraphBreaks);

  // Check if we have any list tags after conversion
  const hasLists = REGEX_PATTERNS.html.listTag.test(withLists);

  if (hasLists) {
    // If we have lists, we need to wrap non-list content in paragraphs
    // Split by list blocks using safe string-based function (avoids ReDoS)
    const segments = splitByListBlocks(withLists);

    return segments
      .map((segment) => {
        // If it's a list block, return as-is
        if (REGEX_PATTERNS.html.listTag.test(segment)) {
          return segment;
        }
        // Otherwise, process as paragraphs
        const trimmed = segment.trim();
        if (!trimmed) {
          return '';
        }
        // Split by double line breaks for paragraphs
        const paragraphs = trimmed.split(REGEX_PATTERNS.formatting.doubleNewline);
        return paragraphs
          .map((p) => {
            const pTrimmed = p.trim();
            if (!pTrimmed) {
              return '';
            }
            // Replace single line breaks with <br>
            const withBreaks = pTrimmed.replace(REGEX_PATTERNS.formatting.singleNewline, '<br>');
            return `<p>${withBreaks}</p>`;
          })
          .filter(Boolean)
          .join('');
      })
      .join('');
  }

  // No lists, process as before
  const paragraphs = text.split(REGEX_PATTERNS.formatting.doubleNewline);

  return paragraphs
    .map((p) => {
      // Replace single line breaks with <br>
      const withBreaks = p.trim().replace(REGEX_PATTERNS.formatting.singleNewline, '<br>');
      return `<p>${withBreaks}</p>`;
    })
    .join('');
}

/**
 * Checks if a string contains HTML tags
 *
 * @param str - The string to check
 * @returns true if the string contains HTML tags
 */
export function containsHtml (str: string): boolean {
  if (!str) {
    return false;
  }
  return REGEX_PATTERNS.html.htmlTag.test(str);
}

/**
 * Strips all HTML tags from a string and returns plain text
 * Also decodes HTML entities
 *
 * @param html - The HTML string to strip
 * @returns Plain text without HTML tags
 *
 * @example
 * stripHtml('<p>Hello <strong>World</strong></p>');
 * // Returns: 'Hello World'
 *
 * @example
 * stripHtml('Day 1 - <p>Conversation starter</p>');
 * // Returns: 'Day 1 - Conversation starter'
 */
export function stripHtml (html: string): string {
  if (!html) {
    return '';
  }

  // First decode any HTML entities
  let text = decodeHtmlEntities(html);

  // Remove all HTML tags (bounded to prevent ReDoS)
  text = text.replace(new RegExp(`<[^>]{0,${MAX_TAG_LENGTH * 10}}>`, 'g'), '');

  // Clean up extra whitespace (multiple spaces, newlines) but preserve single spaces
  text = text.replace(/\s+/g, ' ').trim();

  return text;
}

/**
 * Decodes HTML entities in a string
 * Handles common entities like &lt; &gt; &amp; &quot; &apos; and numeric entities
 *
 * @param str - The string with HTML entities
 * @returns The decoded string
 */
export function decodeHtmlEntities (str: string): string {
  if (!str) {
    return '';
  }

  // Map of common HTML entities
  const entities: Record<string, string> = {
    '&lt;': '<',
    '&gt;': '>',
    '&amp;': '&',
    '&quot;': '"',
    '&apos;': "'",
    '&#39;': "'",
    '&nbsp;': ' ',
    '&#x27;': "'",
    '&#x2F;': '/',
    '&#47;': '/',
  };

  let result = str;

  // Replace named and common numeric entities
  Object.keys(entities).forEach((entity) => {
    result = result.split(entity).join(entities[entity]);
  });

  // Handle numeric entities (decimal): &#123;
  // Bounded to max 7 digits (covers all valid Unicode code points)
  result = result.replace(/&#(\d{1,7});/g, (match, dec) => {
    return String.fromCharCode(parseInt(dec, 10));
  });

  // Handle numeric entities (hex): &#x1A;
  // Bounded to max 6 hex chars (covers all valid Unicode code points)
  result = result.replace(/&#x([0-9a-fA-F]{1,6});/g, (match, hex) => {
    return String.fromCharCode(parseInt(hex, 16));
  });

  return result;
}

/**
 * Checks if a string contains encoded HTML entities that represent HTML tags
 *
 * @param str - The string to check
 * @returns true if the string contains encoded HTML
 */
export function containsEncodedHtml (str: string): boolean {
  if (!str) {
    return false;
  }
  // Check for common encoded HTML patterns like &lt;p&gt; or &lt;div&gt;
  // Bounded quantifiers to prevent ReDoS
  return new RegExp(`&lt;\\s{0,10}[a-z][^&]{0,${MAX_ENTITY_LENGTH}}&gt;`, 'i').test(str);
}

/**
 * Wraps loose text (text not inside HTML tags) in span elements
 * This handles mixed content like "Day 1 - <p>Conversation starter</p>"
 *
 * @param html - The HTML string with potential loose text
 * @returns HTML with loose text wrapped in spans
 */
export function wrapLooseText (html: string): string {
  if (!html) {
    return '';
  }

  // If no HTML tags, return as-is (will be handled by textToHtml)
  if (!containsHtml(html)) {
    return html;
  }

  // Split content by HTML tags, keeping the tags
  // This regex matches HTML tags (opening, closing, self-closing)
  // Bounded quantifier to prevent ReDoS
  const parts = html.split(new RegExp(`(<[^>]{1,${MAX_TAG_LENGTH * 10}}>)`, 'g')).filter(Boolean);

  let result = '';
  let insideTag = false;
  let depth = 0;

  for (const part of parts) {
    if (part.startsWith('<')) {
      // This is an HTML tag
      if (part.startsWith('</')) {
        // Closing tag
        depth = Math.max(0, depth - 1);
      } else if (!part.endsWith('/>')) {
        // Opening tag (not self-closing)
        depth++;
      }
      result += part;
      insideTag = false;
    } else {
      // This is text content
      const trimmed = part.trim();
      if (trimmed && depth === 0) {
        // Loose text at root level - wrap in span
        result += `<span>${part}</span>`;
      } else {
        result += part;
      }
    }
  }

  return result;
}

/**
 * Sanitizes rich text from the backend, auto-detecting if it's HTML or plain text
 * and converting appropriately. Also converts list patterns and adds paragraph breaks.
 * Handles HTML-encoded content (e.g., &lt;p&gt; instead of <p>).
 *
 * @param content - The content string (HTML or plain text)
 * @param options - Optional sanitization options
 * @returns Sanitized HTML ready for rendering
 */
export function sanitizeRichText (content: string, options: ISanitizeOptions = {}): string {
  if (!content) {
    return '';
  }

  // First, check if content contains encoded HTML entities and decode them
  let processedContent = content;
  if (containsEncodedHtml(content)) {
    processedContent = decodeHtmlEntities(content);
  }

  // Check for inline list patterns (even in HTML content)
  const hasInlineUnorderedList = (processedContent.match(REGEX_PATTERNS.lists.dashSeparator) || []).length >= 2;
  const hasInlineOrderedList = (processedContent.match(REGEX_PATTERNS.lists.orderedMarker) || []).length >= 2;

  // If content already contains HTML
  if (containsHtml(processedContent)) {
    let processed = processedContent;

    // First, wrap any loose text that's not inside HTML tags
    processed = wrapLooseText(processed);

    // Add paragraph breaks to long HTML content
    // We need to add breaks and then wrap in <p> tags
    if (processedContent.length > 150 && !processedContent.includes('<p>') && !processedContent.includes('<br')) {
      processed = addParagraphBreaks(processed);
      // Convert the line breaks to HTML paragraphs
      if (processed.includes('\n\n')) {
        const paragraphs = processed.split(REGEX_PATTERNS.formatting.doubleNewline);
        processed = paragraphs.map((p) => `<p>${p.trim()}</p>`).join('');
      }
    }

    // Then handle inline list patterns
    if (hasInlineUnorderedList || hasInlineOrderedList) {
      processed = convertInlineListPatterns(processed);
    }

    return sanitizeHtml(processed, options);
  }

  // Otherwise, convert plain text to HTML paragraphs and sanitize
  const htmlContent = textToHtml(processedContent);
  return sanitizeHtml(htmlContent, options);
}
